import React, { useState, useRef, useEffect } from "react";
import { DropDown } from "../icons";

export default function SelectInput({
  label,
  options = [],
  value,
  onChange,
  placeholder = "Select an option",
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Handle both string and object options
  const getOptionLabel = (opt) => (typeof opt === "string" ? opt : opt.label);
  const getOptionValue = (opt) => (typeof opt === "string" ? opt : opt.value);

  // Find selected label
  const selected = options.find((opt) => getOptionValue(opt) === value);
  const selectedLabel = selected ? getOptionLabel(selected) : placeholder;

  const [searchTerm, setSearchTerm] = useState("");
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const currentIndex = options.findIndex((opt) => getOptionValue(opt) === value);
      let nextIndex = currentIndex;
      if (e.key === "ArrowDown") {
        nextIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0;
      } else {
        nextIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1;
      }
      onChange?.(getOptionValue(options[nextIndex]));
      return;
    }

    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen((s) => !s);
      return;
    }

    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const char = e.key.toLowerCase();
      const newSearchTerm = searchTerm + char;
      setSearchTerm(newSearchTerm);

      const match = options.find((opt) =>
        getOptionLabel(opt).toLowerCase().startsWith(newSearchTerm)
      );

      if (match) {
        onChange?.(getOptionValue(match));
      }

      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => {
        setSearchTerm("");
      }, 500);
    }
  };

  return (
    <div>
      {label && <label className="block text-sm leading-6 text-black">{label}</label>}
      <div className="relative mt-2" ref={menuRef}>
        <button
          onClick={() => setOpen((s) => !s)}
          onKeyDown={handleKeyDown}
          className="w-full flex items-center justify-between leading-6 text-sm  bg-[#F5F5F5] px-4 py-2.5 rounded-[5px] "
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <span className={value ? "text-black capitalize" : "text-[#A2A1A8]"}>
            {selectedLabel}
          </span>
          <DropDown />
        </button>

        {open && (
          <div className="absolute left-0 right-0 mt-2 bg-white border border-gray-200 rounded-md shadow-lg z-30 max-h-60 overflow-y-auto">
            {options.map((opt, idx) => {
              const optValue = getOptionValue(opt);
              const optLabel = getOptionLabel(opt);
              return (
                <button
                  key={idx}
                  onClick={() => {
                    onChange?.(optValue);
                    setOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 capitalize ${optValue === value ? "font-medium text-black bg-[#F0F0F0]" : "font-normal text-[#878686]"
                    }`}
                >
                  {optLabel}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
